import {
  LeagueConfig,
  LEAGUE_MODULE_NAMES,
  Environment,
} from '@leagueplatform/core';
import { createReactRouterAdapter } from '@leagueplatform/react-router-adapter';
import { LoadingSpinner } from '@telus-alberta-health-web/shared-module';
import healthClipboardFilled from 'assets/IllustrativeHealthClipboardFilled.svg';
import { ASSET_OVERRIDES } from 'common/asset-overrides';
import { APP_ID, AUTH0_CONNECTION, TENANT_ID } from 'common/constants';
import EN_LOCALES from 'locales/en.json';
import ES_LOCALES from 'locales/es.json';
import FR_LOCALES from 'locales/fr.json';
import { telusAlbertaTheme } from 'theme/theme';
import JourneyHeaderBackground from 'assets/header-background.png';
import { dfdLiveChatClient } from 'common/dfd-live-chat-client';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { terminateLiveChatSession } from 'utils/terminate-live-chat-session';

export const routesConfig = {
  [LEAGUE_MODULE_NAMES.assessment]: '/member/health-profile/questions',
  [LEAGUE_MODULE_NAMES.healthProfile]: '/member/health-profile',
  [LEAGUE_MODULE_NAMES.healthNeedPages]: '/health-need',
  [LEAGUE_MODULE_NAMES.onboarding]: '/onboarding',
};

const {
  VITE_GA_PROPERTY_ID,
  VITE_SEGMENT_KEY,
  VITE_WS_API_URL,
  VITE_API_URL,
  VITE_LEGACY_REST_API_URL,
  VITE_CONTENT_URL,
  VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID,
  VITE_APP_ENV,
} = import.meta.env;

const authConfig = {
  domain: VITE_AUTH0_DOMAIN!,
  client_id: VITE_AUTH0_CLIENT_ID!,
  redirect_uri: window.location.origin,
  connection: AUTH0_CONNECTION,
};

const analyticsConfig = {
  GAPropertyId: VITE_GA_PROPERTY_ID,
  segmentKey: VITE_SEGMENT_KEY,
};

const apiConfig = {
  wsUrl: VITE_WS_API_URL!,
  url: VITE_API_URL!,
  legacyRestApi: VITE_LEGACY_REST_API_URL!,
};

const appConfig = {
  contentUrl: VITE_CONTENT_URL!,
  appEnvironment: VITE_APP_ENV as Environment,
};

export const config: LeagueConfig = {
  core: {
    tenantId: TENANT_ID,
    clientId: APP_ID,
    auth: {
      clientOptions: authConfig,
      // Note: Any changes to this value have to be synced with the idle_session_lifetime in the auth0 configuration.
      idleSessionLifetimeHours: 10 / 60,
      sessionTimeoutOptions: {
        warningBufferMillis: 30000,
        onSessionExpire: async () => {
          terminateLiveChatSession();
          await StandaloneAuth.client.logout({
            returnTo: window.location.origin,
          });
        },
      },
    },
    analytics: analyticsConfig,
    api: apiConfig,
    ...appConfig,
    routing: {
      adapter: createReactRouterAdapter(),
      handleExternalLink: async (url: string, target?: string) => {
        window.open(url, target ?? '_blank', 'noreferrer');
      },
    },
    ui: {
      themeOptions: {
        customIcons: {
          illustrativeHealthClipboardFilled: healthClipboardFilled,
        },
      },
      theme: telusAlbertaTheme,
      components: {
        pageHeader: {
          shape: 'line',
        },
        loading: {
          element: <LoadingSpinner />,
          text: false,
        },
      },
    },
    i18n: {
      strings: {
        default: EN_LOCALES,
        en: EN_LOCALES,
        es: ES_LOCALES,
        fr: FR_LOCALES,
      },
    },
  },
  assets: {
    overrides: ASSET_OVERRIDES,
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
  },
  healthJourney: {
    tabs: ['activities', 'progress', 'history', 'explore'],
    headerConfig: {
      backgroundImageSrc: JourneyHeaderBackground,
      alignment: 'start',
    },
    activityDetailsConfig: {
      enableFullPageBackground: true,
    },
  },
  liveChat: {
    liveChatClient: dfdLiveChatClient,
    shouldShowNavigateAway: true,
  },
};
